import { Injectable, Inject } from '@angular/core';
import { Title, Meta } from '@angular/platform-browser';
import { DOCUMENT } from '@angular/common';

export interface PageSeoData {
    title: string;
    description: string;
    keywords?: string[];
    type?: string;
    image?: string;
    canonicalUrl?: string;
}

@Injectable({
    providedIn: 'root'
})
export class SeoService {
    constructor(
        private titleService: Title,
        private metaService: Meta,
        @Inject(DOCUMENT) private document: Document
    ) { }

    public setPageData(data: PageSeoData): void {
        this.setTitle(data.title);
        this.setMetaTags(data);
        this.setCanonicalUrl(data.canonicalUrl);
        this.setJsonLd(data);
    }

    private setTitle(title: string): void {
        this.titleService.setTitle(`${title} | LogicGames`);
    }

    private setMetaTags(data: PageSeoData): void {
        // Standard Description
        this.metaService.updateTag({ name: 'description', content: data.description });

        // Keywords (Optional, less important for Google but good for others)
        if (data.keywords && data.keywords.length > 0) {
            this.metaService.updateTag({ name: 'keywords', content: data.keywords.join(', ') });
        }

        // Open Graph (Facebook/LinkedIn)
        this.metaService.updateTag({ property: 'og:title', content: data.title });
        this.metaService.updateTag({ property: 'og:description', content: data.description });
        this.metaService.updateTag({ property: 'og:type', content: data.type || 'website' });
        if (data.image) {
            this.metaService.updateTag({ property: 'og:image', content: data.image });
        }
        // Twitter Card
        this.metaService.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
        this.metaService.updateTag({ name: 'twitter:title', content: data.title });
        this.metaService.updateTag({ name: 'twitter:description', content: data.description });
    }

    private setCanonicalUrl(url?: string): void {
        // Remove existing canonical
        const existingLink: HTMLLinkElement | null = this.document.querySelector('link[rel="canonical"]');
        if (existingLink) {
            existingLink.remove();
        }

        if (url) {
            const link: HTMLLinkElement = this.document.createElement('link');
            link.setAttribute('rel', 'canonical');
            this.document.head.appendChild(link);
            link.setAttribute('href', url);
        }
    }

    private setJsonLd(data: PageSeoData): void {
        // Basic structured data implementation - can be expanded
        const scriptId = 'json-ld-structured-data';
        let script = this.document.getElementById(scriptId) as HTMLScriptElement;

        if (!script) {
            script = this.document.createElement('script');
            script.id = scriptId;
            script.type = 'application/ld+json';
            this.document.head.appendChild(script);
        }

        const jsonLd = {
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": data.title,
            "description": data.description
        };

        script.text = JSON.stringify(jsonLd);
    }
}
